import { ColumnDef } from "@tanstack/react-table";
import { CrossCircleIcon } from "assets/icons";
import { t } from "i18next";
import moment from "moment";
import { Checkbox, TableItemActionsControl } from "ui";

import { AVAILABLE_TO_CANCEL_ORDER_STATUSES } from "../../constants";
import { getOrderPaymentMethodLabel } from "../../helpers";
import { Order } from "../../orders.types";
import { OrderProblemNotesTooltip, OrderStatusCell } from "./atoms";
import { GetColumnsParams } from "./OrdersTable.types";

import styles from "./OrdersTable.module.scss";

export const getResellerProfilePageColumns = ({
  ordersList,
  statusesList,
  getOrderStatusIndex,
  openedItemOptionsIndex,
  selectedOrdersIds,
  setSelectedOrdersIds,
  setOpenedItemOptionsIndex,
  onCancelOrder
}: GetColumnsParams): Array<ColumnDef<Order, Order[]>> => {
  return [
    {
      id: "select",
      header: () => {
        const isChecked = ordersList.some((order) =>
          selectedOrdersIds?.includes(order.id)
        );
        const isFullyChecked = ordersList.every((order) =>
          selectedOrdersIds?.includes(order.id)
        );

        return (
          <Checkbox
            className={styles.ordersTable_checkboxCell}
            checked={isChecked}
            partiallyChecked={!isFullyChecked}
            onChange={() => {
              if (isChecked) {
                setSelectedOrdersIds?.([]);
              } else {
                setSelectedOrdersIds?.((prev) => [
                  ...prev,
                  ...ordersList.map((order) => order.id)
                ]);
              }
            }}
          />
        );
      },
      size: 4,
      minSize: 4,
      cell: ({ row }) => {
        const isSelected = selectedOrdersIds?.includes(row.original.id);

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox
              className={styles.ordersTable_checkboxCell}
              checked={isSelected}
              onChange={() => {
                setSelectedOrdersIds?.((prev) =>
                  isSelected
                    ? prev.filter((id) => id !== row.original.id)
                    : [...prev, row.original.id]
                );
              }}
            />
          </div>
        );
      }
    },
    {
      header: t("Order"),
      size: 16,
      minSize: 16,
      cell: ({ row }) => (
        <div className={styles.ordersTable_orderId}>
          <OrderProblemNotesTooltip
            tooltipId={`problem-notes-tooltip-${row.original.id}`}
            problemNotesForAdmin={row.original.problemNotesForAdmin}
          />
          {row.original.id}
        </div>
      )
    },
    {
      id: "orderTotal",
      accessorKey: "orderTotal",
      header: t("Order total"),
      size: 15,
      minSize: 15,
      cell: ({ row }) => row.original.orderTotal.toLocaleString("en-US")
    },
    {
      id: "totalCommission",
      accessorKey: "totalCommission",
      header: t("Commission"),
      size: 12,
      minSize: 12,
      cell: ({ row }) =>
        Math.floor(row.original.totalCommission).toLocaleString("en-US")
    },
    {
      id: "totalMargin",
      accessorKey: "totalMargin",
      header: t("Margin"),
      size: 12,
      minSize: 12,
      cell: ({ row }) =>
        Math.floor(row.original.totalMargin).toLocaleString("en-US")
    },
    {
      header: t("Payment Method"),
      size: 13,
      minSize: 13,
      cell: ({ row }) => getOrderPaymentMethodLabel(row.original.paymentMethod)
    },
    {
      id: "createdAt",
      accessorKey: "createdAt",
      header: t("Order date"),
      size: 13,
      minSize: 13,
      cell: ({ row }) => (
        <pre>
          {`${moment(row.original.createdAt).format("DD/MM/YYYY")}`}
          {"\n"}
          {`${moment(row.original.createdAt).format("HH:mm")}`}
        </pre>
      )
    },
    {
      header: t("Status"),
      size: 13,
      minSize: 13,
      cell: ({ row }) => (
        <OrderStatusCell
          order={row.original}
          statusesList={statusesList}
          activeStatusIndex={getOrderStatusIndex(row.original.status)}
        />
      )
    },

    {
      accessorKey: "dotsColumn",
      header: () => null,
      size: 3,
      minSize: 3,
      cell: ({ row }) => {
        return AVAILABLE_TO_CANCEL_ORDER_STATUSES.includes(
          row.original.status
        ) ? (
          <TableItemActionsControl
            actionsList={[
              {
                label: t("Cancel"),
                Icon: (
                  <CrossCircleIcon fill={"#090914"} width={18} height={18} />
                ),
                onClick: () => onCancelOrder?.(row.original)
              }
            ]}
            rowIndex={row.index}
            setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
            openedItemOptionsIndex={openedItemOptionsIndex}
          />
        ) : null;
      },
      enableSorting: false
    }
  ];
};
